import {
    useInfiniteQuery,
    useMutation,
    useQueryClient,
} from '@tanstack/react-query';

import {
    deleteRecord,
    fetchRecords,
} from '../services/ProductsService';
import {
    notifyError,
    notifySuccess,
} from '../../../components/ServiceNotifications';

// FNP-918: the data layer for the products list.
//
// Query keys are STRUCTURED and module-scoped (checklist §8). The previous key
// was `['products', currentToken]` — a bare string plus a page token, which
// both collides across modules and makes the cache page-shaped rather than
// list-shaped.
export const productsKeys = {
    all: ['products'] as const,
    lists: () => [...productsKeys.all, 'list'] as const,
    list: (filters: Record<string, string>) =>
        [...productsKeys.lists(), {filters}] as const,
    details: () => [...productsKeys.all, 'detail'] as const,
    detail: (id: string) => [...productsKeys.details(), id] as const,
};

// Page size is a SERVER concern under token pagination — it is the `limit` sent
// with the query, fixed by the module. There is deliberately no rows-per-page
// dropdown: there is no page to resize and no total to divide (checklist §9).
const PAGE_SIZE = 50;

/**
 * FNP-918: TOKEN PAGINATION.
 *
 * This was `useQuery` + the app-local `usePagination` token stack, which read
 * as compliant (a query hook, a page control, forward/back buttons) while being
 * a page-at-a-time model the shared footer cannot describe.
 * `useInfiniteQuery` concatenates pages instead, so the client holds a growing
 * loaded window and the footer can honestly say how much of it it has seen.
 *
 * ⚠️ `getNextPageParam` MUST return `undefined` — not `null`, and never the
 * same token back — when there are no more pages. Returning `null` leaves
 * `hasNextPage` true and react-query refetches forever.
 *
 * ⚠️ `listProducts` takes NO filter argument at all — unlike listInvoices and
 * listPrices, its signature is `($limit, $nextToken)` only. So every narrowing
 * this module does is client-side, and there is no filters value to put in the
 * queryKey. The empty object is passed to keep the key shape identical to its
 * sibling modules.
 */
export const useProductsInfiniteQuery = () => {
    return useInfiniteQuery({
        queryKey: productsKeys.list({}),
        queryFn: ({pageParam}: {pageParam: string | null}) =>
            fetchRecords({}, PAGE_SIZE, pageParam ?? undefined),
        getNextPageParam: (lastPage) => lastPage?.nextToken ?? undefined,
        // Explicit annotation, or `pageParam` type-errors (§9).
        initialPageParam: null as string | null,
    });
};

/**
 * Batch delete. The list key is invalidated on success so the table reflects
 * the removal without a manual reload (§8).
 */
export const useDeleteProductsMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (ids: string[]) =>
            Promise.all(ids.map(id => deleteRecord(id))),
        onSuccess: (_data, ids) => {
            notifySuccess(
                ids.length === 1
                    ? 'Product deleted'
                    : `${ids.length} products deleted`,
            );
            queryClient.invalidateQueries({queryKey: productsKeys.all});
        },
        onError: notifyError,
    });
};
